import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  /** Swag Chic: counts variant changes, so a slower earlier photo never replaces a newer one */
  #swapCount = 0;

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Swag Chic: keep the current photo on screen until the new first photo has loaded (at most 1.5 s), so a
        // colour change no longer flashes an empty tile. Only the latest choice is swapped in.
        const swap = ++this.#swapCount;
        return readyFirstImage(newMediaGallery).then(() => {
          if (swap === this.#swapCount && this.isConnected) this.replaceWith(newMediaGallery);
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}


/**
 * Swag Chic: resolves when the first photo of a gallery fetched for a variant change is decoded, or after 1.5 s.
 * @param {Element} gallery
 * @returns {Promise<void>}
 */
function readyFirstImage(gallery) {
  const img = gallery.querySelector('img');
  if (!(img instanceof HTMLImageElement) || !(img.currentSrc || img.src)) return Promise.resolve();
  const probe = new Image();
  // the same candidate the page will pick: "auto" sizing only works on lazy images in the page, so drop it
  probe.sizes = (img.getAttribute('sizes') || '').replace(/^auto\s*,?\s*/, '') || '100vw';
  if (img.srcset) probe.srcset = img.srcset;
  probe.src = img.src;
  return Promise.race([probe.decode().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 1500))]).then(() => {});
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
